import React from 'react'
import { Edit, Star, Trash2 } from 'lucide-react'

import { InfoContactWindow } from 'src/features/InfoContactWindow'
import { AlertContactUpdateWindow } from 'src/features/AlertContactUpdateWindow'
import AlertDialogDemo from 'src/components/CustomAlertDialog'

import { ICON_SIZE } from 'src/features/TableForContactsHistory/constants'
import { useContactsTableSection } from 'src/features/ContactTableSection/useContactsTableSection'
import { ContactsTableSectionType } from 'src/features/ContactTableSection/types'
import { hideTableColumn } from 'src/features/ContactTableSection/helpers'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/shadcn/table'
import { Badge } from 'src/components/shadcn/badge'

export const ContactsTableSection = ({
  contacts,
  serviceTypeDictionary,
  editModeMap,
  toggleEditMode,
  handleFavorite,
  handleDelete,
}: ContactsTableSectionType): JSX.Element => {
  const {
    locale,
    tableHeaders,
    isMobile,
    clickedContact,
    handleContactClick,
    toggleModal,
    isOpen,
    titles,
    hiddenOnMobile,
    updateClickedContact,
    handlePhoneClick,
    mainThemeColor,
  } = useContactsTableSection()

  return (
    <>
      <Table className="w-full table-fixed mb-4">
        <TableHeader>
          <TableRow>
            {tableHeaders.map(label => (
              <TableHead
                key={label}
                className={hideTableColumn(isMobile, label, titles)}
              >
                {label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {contacts.map(contact => (
            <TableRow
              key={contact.id}
              className="cursor-pointer"
              onClick={() => handleContactClick(contact)}
            >
              <TableCell className="font-medium">
                {contact.contactName}
              </TableCell>
              <TableCell>
                <Badge
                  variant="outline"
                  className="cursor-pointer"
                  style={{ color: mainThemeColor, borderColor: mainThemeColor }}
                  onClick={event =>
                    handlePhoneClick(event, contact.phoneNumber)
                  }
                >
                  {contact.phoneNumber}
                </Badge>
              </TableCell>
              <TableCell className={hiddenOnMobile}>
                {serviceTypeDictionary[contact.serviceType]}
              </TableCell>
              <TableCell
                className={`max-w-[150px] overflow-hidden text-ellipsis whitespace-nowrap ${hiddenOnMobile}`}
              >
                <span className="block max-w-[150px] overflow-hidden text-ellipsis whitespace-nowrap">
                  {contact.address}
                </span>
              </TableCell>
              <TableCell
                className={`max-w-[150px] overflow-hidden text-ellipsis whitespace-nowrap ${hiddenOnMobile}`}
              >
                <span className="block max-w-[150px] overflow-hidden text-ellipsis whitespace-nowrap">
                  {contact.additionalNotes}
                </span>
              </TableCell>
              <TableCell onClick={event => event.stopPropagation()}>
                <div className="flex justify-center gap-2">
                  <button
                    className={`text-gray-500 ${contact.isFavorite ? 'text-yellow-400' : 'hover:text-yellow-400'}`}
                    onClick={() => handleFavorite(contact.id, contact)}
                  >
                    <Star
                      size={ICON_SIZE}
                      fill={contact.isFavorite ? 'currentColor' : 'transparent'}
                    />
                  </button>
                  <AlertContactUpdateWindow
                    itemId={contact.id}
                    isOpen={editModeMap[contact.id]}
                    toggle={() => toggleEditMode(contact.id)}
                    address={contact.address}
                    contactName={contact.contactName}
                    phoneNumber={contact.phoneNumber}
                    serviceType={contact.serviceType}
                    additionalNotes={contact.additionalNotes}
                    onSave={updateClickedContact}
                  />
                  <button
                    className="text-gray-500 hover:text-blue-500"
                    onClick={() => toggleEditMode(contact.id)}
                  >
                    <Edit size={ICON_SIZE} />
                  </button>
                  <AlertDialogDemo
                    trigger={
                      <button className="text-gray-500 hover:text-red-500">
                        <Trash2 size={ICON_SIZE} />
                      </button>
                    }
                    onConfirm={() => handleDelete(contact.id)}
                    accept={locale.alert.accept}
                    description={locale.alert.description}
                    title={locale.alert.title}
                    cancel={locale.alert.cancel}
                  />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {clickedContact && (
        <InfoContactWindow
          contact={clickedContact}
          isOpen={isOpen}
          toggle={toggleModal}
          toggleEditMode={toggleEditMode}
        />
      )}
    </>
  )
}
